/**
 * 서비스 관리 — 콘솔 목록 공통 규격 채택본 (2026-09-09 사용자 지시).
 *
 * 이전 화면은 **카드 격자 세 절**이 세로로 쌓여 있었다(내장 · 사용자 정의 · 외부 연결).
 * 세 절은 서로 다른 표라 **좌 분류로 접는다** — 형 B. 한 번에 한 갈래만 본다.
 *
 * ★ 갈래마다 «할 수 있는 일»이 다르다 — 규격을 그대로 찍지 않고 갈래가 정하게 둔다.
 *   · 내장 서비스   — **등록이 없다**(chat-server 의 고정 enum 이라 늘릴 수 없다).
 *                     세부설정(JSON)은 **팝업**이 받는다 — 카드 안에서 펼쳐지던 것을 옮겼다.
 *   · 사용자 정의   — 등록·수정·삭제. 폼은 `CustomServiceModal` 그대로.
 *   · 외부 연결     — 등록·수정·삭제 + **연결 테스트**(줄 끝 작업 하나가 더 있다).
 *
 * ⚠ 이전 화면 `ServiceModuleSection` 은 되돌리기 대비로 남겨 뒀고, 등록·편집 폼 두 벌
 *   (`CustomServiceModal` · `ExternalServiceModal`)은 그 파일 것을 그대로 연다.
 * ⚠ 서버 변경 0 · 마이그레이션 0 — 부르는 라우트가 이전과 똑같다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import './ServiceModules.css';
import {
  Mail, Calendar, CheckSquare, LayoutGrid, MessageCircle,
  Settings, Globe, Pencil, Trash2, X, Plus, Zap, CheckCircle, XCircle,
} from '../../../icons';
import {
  listServiceModules, toggleServiceModule,
  type ServiceModuleItem, type ModuleKey,
} from '../../../api/adminConsole';
import {
  listExternalServices, updateExternalService, deleteExternalService, testExternalService,
  listCustomServices, updateCustomService, deleteCustomService,
  type ExternalServiceView, type ServiceTestResult, type CustomService,
} from '../../../api/externalServices';
import { tAdmin, subscribeAdminLang, getAdminLang } from '../../../lib/admin-i18n';
import { getDateLocale } from '../../../lib/base-i18n';
import { CustomServiceModal, ExternalServiceModal } from './ServiceModuleSection';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';

const MODULE_LABEL_KEYS: Record<ModuleKey, string> = {
  mail: 'admin.module.label.mail',
  calendar: 'admin.module.label.calendar',
  task: 'admin.module.label.task',
  workspace: 'admin.module.label.workspace',
  workchat: 'admin.module.label.workchat',
};
const MODULE_ICONS: Record<ModuleKey, () => JSX.Element> = {
  mail: () => <Mail size={16} />,
  calendar: () => <Calendar size={16} />,
  task: () => <CheckSquare size={16} />,
  workspace: () => <LayoutGrid size={16} />,
  workchat: () => <MessageCircle size={16} />,
};

type Cat = 'internal' | 'custom' | 'external';

function moduleErr(e: any, fallback: string): string {
  const d = e?.response?.data;
  if (d?.code === 'GRACE_ACTIVE') return '회사 가입 후 24시간 동안은 변경이 제한됩니다.';
  if (d?.code === 'INSTITUTION_FROZEN') return '회사가 freeze 상태입니다.';
  return d?.message ?? d?.error ?? fallback;
}

/**
 * ★ 2026-09-17 — `opsAllowed=false`(회사 관리자)면 **「사용자 정의」·「외부 연결」 두 갈래를 감춘다.**
 *   그 둘의 저장소는 **회사 칸이 없는 인스턴스 전역 한 벌**이라 서버가 SYSTEM_ADMIN 으로 잠겼다
 *   (`admin/backend/src/routes/services.ts`). 탭만 남기면 **「눌렀는데 403」**이 된다.
 *
 * ⚠ 그래서 **탭(`'modules'`) 자체는 감추지 않는다** — 첫 갈래 「내장 서비스」는 성격이 다르다:
 *   `tenant_service_modules`(회사별 표)를 chat-server 가 `['ADMIN','SYSTEM_ADMIN']` 으로 열어 둔
 *   **회사 관리자의 정당한 기능**이다(`apps/chat-server/src/modules/admin/service-module.routes.ts:51`).
 *   탭을 통째로 `OPS_ONLY_TABS` 에 넣으면 **오늘까지 되던 그 기능이 내일 안 보인다.**
 *
 * ⚠ 서버 가드가 진짜 방어다. 여기서 감추는 것은 **친절**일 뿐 — 라우트를 직접 부르면 그만이다.
 */
export function ServiceListSection({ opsAllowed = true }: { opsAllowed?: boolean } = {}) {
  const [, setLangBump] = useState(getAdminLang());
  useEffect(() => subscribeAdminLang(() => setLangBump(getAdminLang())), []);

  const [cat, setCat] = useState<Cat>('internal');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [modules, setModules] = useState<ServiceModuleItem[]>([]);
  const [customs, setCustoms] = useState<CustomService[]>([]);
  const [externals, setExternals] = useState<ExternalServiceView[]>([]);
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState<Record<string, ServiceTestResult | 'loading'>>({});

  /** 세부설정(JSON) 팝업 — 카드 안에서 펼쳐지던 것을 옮겼다. */
  const [config, setConfig] = useState<ServiceModuleItem | null>(null);
  const [customOpen, setCustomOpen] = useState<false | { row: CustomService | null }>(false);
  const [extOpen, setExtOpen] = useState<false | { row: ExternalServiceView | null }>(false);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    // ★ 운영자가 아니면 잠긴 둘은 아예 부르지 않는다 — 부르면 403 두 번이 날 뿐이다.
    //   (allSettled 라 화면이 깨지지는 않았지만, 안 쓸 것을 묻지 않는 편이 맞다.)
    const [m, c, x] = await Promise.allSettled([
      listServiceModules(),
      opsAllowed ? listCustomServices() : Promise.resolve([]),
      opsAllowed ? listExternalServices() : Promise.resolve([]),
    ]);
    if (m.status === 'fulfilled') setModules(m.value); else setError(moduleErr(m.reason, '서비스 목록 불러오기 실패'));
    if (c.status === 'fulfilled') setCustoms(c.value);
    if (x.status === 'fulfilled') setExternals(x.value);
    setLoading(false);
  }, [opsAllowed]);
  useEffect(() => { void reload(); }, [reload]);

  const needle = q.trim().toLowerCase();
  const shownModules = useMemo(
    () => modules.filter((m) => !needle || `${m.moduleKey} ${tAdmin((MODULE_LABEL_KEYS[m.moduleKey] ?? m.moduleKey) as any)}`.toLowerCase().includes(needle)),
    [modules, needle],
  );
  const shownCustoms = useMemo(
    () => customs.filter((s) => !needle || `${s.name} ${s.description ?? ''}`.toLowerCase().includes(needle)),
    [customs, needle],
  );
  const shownExternals = useMemo(
    () => externals.filter((s) => !needle || `${s.name} ${s.base_url}`.toLowerCase().includes(needle)),
    [externals, needle],
  );

  async function toggleModule(it: ServiceModuleItem) {
    setBusy(it.moduleKey); setError(null);
    try {
      const u = await toggleServiceModule(it.moduleKey, { enabled: !it.enabled });
      setModules((arr) => arr.map((i) => (i.moduleKey === it.moduleKey ? { ...i, enabled: u.enabled, updatedAt: u.updatedAt } : i)));
    } catch (e: any) { setError(moduleErr(e, '저장 실패')); }
    finally { setBusy(null); }
  }
  async function toggleCustom(s: CustomService) {
    setBusy(s.id);
    try {
      const u = await updateCustomService(s.id, { enabled: !s.enabled });
      setCustoms((arr) => arr.map((x) => (x.id === s.id ? u : x)));
    } catch (e: any) { setError(moduleErr(e, '변경 실패')); }
    finally { setBusy(null); }
  }
  async function removeCustom(s: CustomService) {
    if (!window.confirm(`"${s.name}" 서비스를 삭제할까요?`)) return;
    setBusy(s.id);
    try { await deleteCustomService(s.id); await reload(); }
    catch (e: any) { setError(moduleErr(e, '삭제 실패')); }
    finally { setBusy(null); }
  }
  async function toggleExternal(s: ExternalServiceView) {
    setBusy(s.id);
    try {
      const u = await updateExternalService(s.id, { enabled: !s.enabled });
      setExternals((arr) => arr.map((x) => (x.id === s.id ? u : x)));
    } catch (e: any) { setError(moduleErr(e, '변경 실패')); }
    finally { setBusy(null); }
  }
  async function removeExternal(s: ExternalServiceView) {
    if (!window.confirm(`"${s.name}" 연결을 삭제할까요?`)) return;
    setBusy(s.id);
    try { await deleteExternalService(s.id); await reload(); }
    catch (e: any) { setError(moduleErr(e, '삭제 실패')); }
    finally { setBusy(null); }
  }
  async function runTest(id: string) {
    setTests((t) => ({ ...t, [id]: 'loading' }));
    try {
      const r = await testExternalService(id);
      setTests((t) => ({ ...t, [id]: r }));
    } catch (e: any) {
      setTests((t) => ({ ...t, [id]: { ok: false, latency_ms: 0, detail: e?.message ?? '테스트 실패' } }));
    }
  }

  const CATS = [
    { key: 'internal' as Cat, label: '내장 서비스', count: modules.length, icon: <Settings size={16} /> },
    // ★ 잠긴 둘 — 운영자에게만 그린다(위 주석). 서버가 진짜 방어다.
    ...(opsAllowed ? [
      { key: 'custom' as Cat, label: '사용자 정의', count: customs.length, icon: <Plus size={16} /> },
      { key: 'external' as Cat, label: '외부 연결', count: externals.length, icon: <Globe size={16} /> },
    ] : []),
  ];
  const desc = cat === 'internal'
    ? '메일·캘린더·할일·워크스페이스·채팅 모듈 on/off — 플랫폼 고정 항목이라 늘리거나 지울 수 없습니다.'
    : cat === 'custom'
      ? '관리자가 추가하는 서비스 항목(이름·설명·세부설정 JSON·on/off).'
      : '외부 업무 서버 + 보안 토큰 등록 · 연결 테스트.';

  return (
    <>
      <ConsoleListScreen
        title="서비스 관리"
        kpi={opsAllowed
          ? `내장 ${modules.length} · 사용자 정의 ${customs.length} · 외부 ${externals.length}`
          : `내장 ${modules.length}`}
        desc={desc}
        search={{ value: q, onChange: setQ, placeholder: '서비스 이름으로 찾기' }}
        /* ★ 내장 서비스에는 등록이 없다 — 고정 enum 이라 늘릴 수 없다. 없는 단추를 그리지 않는다. */
        add={cat === 'internal' ? undefined : {
          label: cat === 'custom' ? '서비스 등록' : '연결 등록',
          onClick: () => (cat === 'custom' ? setCustomOpen({ row: null }) : setExtOpen({ row: null })),
        }}
        side={{ head: '갈래', active: cat, onChange: (k) => setCat(k as Cat), items: CATS }}
        notice={error ? <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div> : null}
      >
        {loading ? (
          <div className="cl-empty">{tAdmin('admin.llm.loading')}</div>
        ) : cat === 'internal' ? (
          <ConsoleListTable
            rows={shownModules}
            rowKey={(m) => m.moduleKey}
            onRowClick={(m) => setConfig(m)}
            empty={<>내장 서비스를 불러오지 못했습니다.</>}
            columns={[
              {
                key: 'name', header: '모듈',
                render: (m) => {
                  const Icon = MODULE_ICONS[m.moduleKey] ?? (() => <Settings size={16} />);
                  return (
                    <span className="cl-name">
                      <span className="cl-name__ico"><Icon /></span>
                      <span className="cl-name__t">{tAdmin((MODULE_LABEL_KEYS[m.moduleKey] ?? m.moduleKey) as any)}</span>
                      <span className="cl-name__s">{m.moduleKey}</span>
                    </span>
                  );
                },
              },
              {
                key: 'on', header: '상태',
                render: (m) => (
                  <span className={`cl-badge${m.enabled ? ' is-on' : ''}`}>
                    {m.enabled ? tAdmin('admin.module.status.enabled') : tAdmin('admin.module.status.disabled')}
                  </span>
                ),
              },
              {
                key: 'cfg', header: '세부설정',
                render: (m) => <span className="cl-name__s">{Object.keys(m.configuration ?? {}).length}개 키</span>,
              },
              {
                key: 'at', header: '바뀐 때',
                render: (m) => (m.updatedAt ? new Date(m.updatedAt).toLocaleString(getDateLocale()) : '—'),
              },
            ]}
            /* 줄 끝 작업은 **아이콘만** — 뜻은 aria-label·title 이 진다. */
            rowActions={(m) => (
              <>
                <button
                  type="button" className="cl-rowbtn"
                  aria-label={`${m.moduleKey} 세부설정`} title="세부설정"
                  onClick={() => setConfig(m)}
                >
                  <Settings size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" disabled={busy === m.moduleKey}
                  aria-label={m.enabled ? tAdmin('admin.module.action.disable') : tAdmin('admin.module.action.enable')}
                  title={m.enabled ? tAdmin('admin.module.action.disable') : tAdmin('admin.module.action.enable')}
                  onClick={() => void toggleModule(m)}
                >
                  {m.enabled ? <XCircle size={13} /> : <CheckCircle size={13} />}
                </button>
              </>
            )}
          />
        ) : cat === 'custom' ? (
          <ConsoleListTable
            rows={shownCustoms}
            rowKey={(s) => s.id}
            onRowClick={(s) => setCustomOpen({ row: s })}
            empty={customs.length === 0
              ? <>추가된 사용자 정의 서비스가 없습니다.<span className="cl-empty__sub">위의 「서비스 등록」으로 만듭니다.</span></>
              : <>찾는 서비스가 없습니다.</>}
            columns={[
              {
                key: 'name', header: '서비스',
                render: (s) => (
                  <span className="cl-name">
                    <span className="cl-name__ico"><Settings size={16} /></span>
                    <span className="cl-name__t">{s.name}</span>
                    {s.description && <span className="cl-name__s">{s.description}</span>}
                  </span>
                ),
              },
              { key: 'on', header: '상태', render: (s) => <span className={`cl-badge${s.enabled ? ' is-on' : ''}`}>{s.enabled ? '활성' : '비활성'}</span> },
              { key: 'cfg', header: '세부설정', render: (s) => <span className="cl-name__s">{Object.keys(s.configuration ?? {}).length}개 키</span> },
            ]}
            rowActions={(s) => (
              <>
                <button
                  type="button" className="cl-rowbtn" aria-label={`${s.name} 수정`} title="수정"
                  onClick={() => setCustomOpen({ row: s })}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" disabled={busy === s.id}
                  aria-label={s.enabled ? '비활성화' : '활성화'} title={s.enabled ? '비활성화' : '활성화'}
                  onClick={() => void toggleCustom(s)}
                >
                  {s.enabled ? <XCircle size={13} /> : <CheckCircle size={13} />}
                </button>
                <button type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busy === s.id}
                  aria-label={`${s.name} 삭제`} title="삭제" onClick={() => void removeCustom(s)}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        ) : (
          <ConsoleListTable
            rows={shownExternals}
            rowKey={(s) => s.id}
            onRowClick={(s) => setExtOpen({ row: s })}
            empty={externals.length === 0
              ? <>등록된 외부 서비스가 없습니다.<span className="cl-empty__sub">위의 「연결 등록」으로 만듭니다.</span></>
              : <>찾는 연결이 없습니다.</>}
            columns={[
              {
                key: 'name', header: '연결',
                render: (s) => (
                  <span className="cl-name">
                    <span className="cl-name__ico"><Globe size={16} /></span>
                    <span className="cl-name__t">{s.name}</span>
                    <span className="cl-name__s">{s.base_url}{s.health_path}</span>
                  </span>
                ),
              },
              {
                key: 'on', header: '상태',
                render: (s) => {
                  const t = tests[s.id];
                  if (t === 'loading') return <span className="cl-badge">테스트 중…</span>;
                  if (t == null) return <span className={`cl-badge${s.enabled ? ' is-on' : ''}`}>{s.enabled ? '활성' : '비활성'}</span>;
                  return t.ok
                    ? <span className="cl-badge is-on">정상 {t.latency_ms}ms</span>
                    : <span className="cl-badge" title={t.detail}>연결 실패</span>;
                },
              },
              {
                key: 'token', header: '토큰',
                render: (s) => <span className="cl-name__s">{s.has_token ? `${s.token_preview} · ${s.token_scheme}` : '없음'}</span>,
              },
            ]}
            /**
             * ⚠ 이 갈래만 줄 끝 작업이 넷이다 — 정본 P3 은 「줄마다 최대 3개, 넘으면 ⋮ 로
             *   접는다」고 한다. 넷째(연결 테스트)를 빼면 이 화면의 고유 기능이 사라지고,
             *   활성/비활성을 빼면 이전 화면이 갖고 있던 스위치를 잃는다.
             *   2026-09-09 부터는 **넷 다 아이콘 24×24** 라 넷이어도 96px 다 — 글자가 있던
             *   때보다 좁다. ⋮ 접기 부품이 생기면 그때 규격대로 접는다.
             */
            rowActions={(s) => (
              <>
                <button
                  type="button" className="cl-rowbtn" disabled={tests[s.id] === 'loading'}
                  aria-label={`${s.name} 연결 테스트`} title="연결 테스트"
                  onClick={() => void runTest(s.id)}
                >
                  <Zap size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" aria-label={`${s.name} 수정`} title="수정"
                  onClick={() => setExtOpen({ row: s })}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn" disabled={busy === s.id}
                  aria-label={s.enabled ? '비활성화' : '활성화'} title={s.enabled ? '비활성화' : '활성화'}
                  onClick={() => void toggleExternal(s)}
                >
                  {s.enabled ? <XCircle size={13} /> : <CheckCircle size={13} />}
                </button>
                <button type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busy === s.id}
                  aria-label={`${s.name} 삭제`} title="삭제" onClick={() => void removeExternal(s)}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        )}
      </ConsoleListScreen>

      {config && (
        <ModuleConfigModal
          item={config}
          onClose={() => setConfig(null)}
          onSaved={(updated) => {
            setModules((arr) => arr.map((i) => (i.moduleKey === updated.moduleKey ? { ...i, configuration: updated.configuration, updatedAt: updated.updatedAt } : i)));
            setConfig(null);
          }}
        />
      )}
      {customOpen && (
        <CustomServiceModal
          existing={customOpen.row}
          onClose={() => setCustomOpen(false)}
          onSaved={() => { setCustomOpen(false); void reload(); }}
        />
      )}
      {extOpen && (
        <ExternalServiceModal
          existing={extOpen.row}
          onClose={() => setExtOpen(false)}
          onSaved={() => { setExtOpen(false); void reload(); }}
        />
      )}
    </>
  );
}

/**
 * 내장 모듈 세부설정(JSON) — 카드 안에서 펼쳐지던 편집기를 팝업으로 옮겼다.
 * 담기는 값·오류 문구는 이전과 같다(자유형 object, 모듈 on/off 는 여기서 다루지 않는다).
 */
function ModuleConfigModal({ item, onClose, onSaved }: {
  item: ServiceModuleItem;
  onClose: () => void;
  onSaved: (updated: ServiceModuleItem) => void;
}) {
  const [draft, setDraft] = useState(() => JSON.stringify(item.configuration ?? {}, null, 2));
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(draft || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error('object 형식이어야 합니다');
    } catch (e: any) { setErr(`JSON 파싱 오류: ${e?.message ?? '형식을 확인하세요'}`); return; }
    setSaving(true); setErr(null);
    try { onSaved(await toggleServiceModule(item.moduleKey, { configuration: parsed })); }
    catch (e: any) { setErr(moduleErr(e, '설정 저장 실패')); }
    finally { setSaving(false); }
  }

  return (
    <div className="cl-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="cl-modal" role="dialog" aria-modal="true" aria-label="세부설정" onClick={(e) => e.stopPropagation()}>
        <div className="cl-modal__head">
          <h3>세부설정 — {tAdmin((MODULE_LABEL_KEYS[item.moduleKey] ?? item.moduleKey) as any)}</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="cl-modal__body">
          <div className="cl-field">
            <label htmlFor="cl-mod-cfg">설정(JSON)</label>
            <textarea
              id="cl-mod-cfg" rows={10} value={draft} spellCheck={false}
              aria-label={`${item.moduleKey} configuration JSON`}
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', resize: 'vertical' }}
              onChange={(e) => setDraft(e.target.value)}
            />
            <span className="cl-field__hint">
              이 모듈의 회사별 옵션을 담는 자유형 설정입니다. 키 형식 제한이 없으며, 모듈 on/off 는
              목록의 줄 끝 단추가 담당합니다. (예: {'{ "retentionDays": 90 }'})
            </span>
          </div>
          {err && <div className="cl-modal__alert" role="alert">{err}</div>}
        </div>
        <div className="cl-modal__foot">
          <button type="button" className="cl-modal__btn" onClick={onClose}>취소</button>
          <button type="button" className="cl-modal__btn cl-modal__btn--primary" disabled={saving} onClick={() => void save()}>
            {saving ? '저장 중…' : '설정 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
